import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'galagame',
  url: 'https://mkgcgcb.cn',
  description: 'galagame 是你不容错过的游戏平台，提供丰富的游戏内容、极致的游戏体验与高额奖励。',
  foundingDate: '2020',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+86-400-888-0000',
    contactType: 'customer service',
    email: 'support@galagame.com',
    availableLanguage: ['Chinese'],
  },
};

const milestones = [
  { year: '2020', title: 'galagame 正式成立', desc: '创始团队怀揣着对游戏的热爱，成立了 galagame 游戏平台，开启了一段不凡的征程。' },
  { year: '2021', title: '平台用户突破100万', desc: '凭借优质的游戏内容和极致的用户体验，galagame 迅速积累了首批忠实玩家。' },
  { year: '2022', title: '上线500+款精品游戏', desc: '与全球知名游戏开发商达成合作，游戏库扩充至500款以上，覆盖所有主流游戏类型。' },
  { year: '2023', title: '电竞赛事体系建立', desc: 'galagame 电竞品牌正式发布，首届赛事总奖金突破500万元，吸引众多职业战队参赛。' },
  { year: '2024', title: '注册用户突破1000万', desc: 'galagame 成为国内领先的游戏平台之一，月活跃用户超过500万。' },
  { year: '2025', title: '全球战略启动', desc: 'galagame 开启全球化布局，推出多语言版本，向世界展示中国游戏平台的力量。' },
];

const testimonials = [
  {
    quote: 'galagame 是我用过最好的游戏平台，游戏库丰富得让人惊讶，而且每次活动都特别给力，奖励拿到手软！',
    author: '游戏达人小王',
    role: 'galagame 钻石会员',
    rating: 5,
  },
  {
    quote: '作为一个竞技游戏爱好者，galagame 的电竞赛事真的让我找到了归属感。赛事组织专业，奖金丰厚，强烈推荐！',
    author: '电竞选手Alex',
    role: 'galagame 电竞赛事冠军',
    rating: 5,
  },
  {
    quote: '之前在别的平台玩，总是遇到卡顿和掉线。换到 galagame 之后体验完全不一样了，画面流畅得没话说。',
    author: '休闲玩家小美',
    role: 'galagame 黄金会员',
    rating: 5,
  },
  {
    quote: 'galagame 的客服真的没得说，有一次账号问题半夜联系客服，居然秒回！这种服务态度让人特别安心。',
    author: '资深玩家大刘',
    role: 'galagame 铂金会员',
    rating: 5,
  },
  {
    quote: '从注册到下载到开玩，整个流程特别顺畅。galagame 的界面设计也很用心，找游戏特别方便。',
    author: '新用户小雨',
    role: 'galagame 白银会员',
    rating: 5,
  },
  {
    quote: 'galagame 的会员体系是真的香，每个月送的福利比会员费还多。尤其是钻石会员的周边礼盒，每次都是惊喜。',
    author: '收藏控阿杰',
    role: 'galagame 钻石会员',
    rating: 5,
  },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      alert('留言已发送，感谢你的反馈！');
      form.reset();
      return;
    }
    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d9qo9dsnlsngrm4lm2qg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        alert('留言已发送，感谢你的反馈！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '发送失败，请稍后重试';
        alert(serverMsg);
      }
    } catch {
      alert('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <PageSEO
        title="关于 galagame - 品牌介绍、口碑与联系方式"
        description="了解 galagame 游戏平台的品牌故事、发展历程与用户口碑。galagame 致力于为玩家提供极致游戏体验，联系我们获取更多信息。"
        keywords="galagame关于,galagame品牌,galagame联系方式,galagame口碑,galagame介绍"
        canonicalUrl="https://mkgcgcb.cn/about"
        schema={organizationSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: '关于我们' },
          ]}
        />

        <section id="brand" className="py-10 md:py-16 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-100">
                关于 galagame
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                galagame 成立于2020年，是一个专注于为玩家提供极致游戏体验的综合性游戏平台。
                我们汇聚了超过500款精品游戏，服务超过1200万注册玩家，致力于打造中国领先的游戏社区。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-16">
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-100 mb-4">
                  我们的使命
                </h2>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  galagame 的使命是让每一位玩家都能找到属于自己的游戏乐趣。我们相信游戏不仅是一种娱乐方式，
                  更是一种连接人与人、激发创造力、传递快乐的文化力量。
                </p>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  我们与全球顶尖游戏开发商紧密合作，严格筛选每一款上架游戏，确保平台上的每一款游戏都经过精心打磨。
                  同时，galagame 打造了完整的电竞生态体系，为竞技玩家提供专业舞台。
                </p>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  未来，galagame 将继续拓展全球市场，用技术创新驱动游戏体验升级，让更多人感受到游戏的无限魅力。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Futuristic%20gaming%20headquarters%20with%20modern%20architectural%20design%2C%20clean%20minimalist%20interior%20with%20gaming%20stations%2C%20warm%20ambient%20lighting%2C%20professional%20creative%20workspace%2C%20dark%20tones%20with%20warm%20orange%20accent%20lighting%2C%20corporate%20but%20youthful%20vibe&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="galagame 品牌形象"
                  title="galagame 品牌 - 游戏平台"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="mb-16">
              <h2 className="font-heading text-xl font-bold text-foreground-100 text-center mb-8">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((item, index) => (
                    <div
                      key={item.year}
                      className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                        <div className={`hidden md:block ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                          <span className="font-heading text-lg font-bold text-primary-500">{item.year}</span>
                          <h3 className="font-heading text-base font-semibold text-foreground-100 mt-1">{item.title}</h3>
                          <p className="text-sm text-foreground-500 mt-1 leading-relaxed">{item.desc}</p>
                        </div>
                        <div className="md:hidden">
                          <span className="font-heading text-lg font-bold text-primary-500">{item.year}</span>
                          <h3 className="font-heading text-base font-semibold text-foreground-100 mt-1">{item.title}</h3>
                          <p className="text-sm text-foreground-500 mt-1 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                      <div className="absolute left-[13px] md:left-1/2 top-1.5 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 md:-translate-x-1.5 z-10"></div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="reputation" className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100">
                玩家口碑
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                来自 galagame 玩家的真实评价
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-xl p-5 border border-background-200/50"
                >
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm text-primary-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                    &ldquo;{item.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200/30">
                    <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-500">
                        {item.author.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-200">{item.author}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100">
                联系我们
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                有任何问题或建议？我们随时为你服务
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
              <div className="bg-background-100 rounded-xl p-5 text-center border border-background-200/50">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-100 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500">support@galagame.com</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center border border-background-200/50">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-500/10">
                  <i className="ri-phone-line text-xl text-accent-500"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-100 mb-1">客服热线</h3>
                <p className="text-xs text-foreground-500">400-888-0000</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center border border-background-200/50">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-500/10">
                  <i className="ri-time-line text-xl text-secondary-500"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-100 mb-1">服务时间</h3>
                <p className="text-xs text-foreground-500">7x24小时在线</p>
              </div>
            </div>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-xl p-6 border border-background-200/50"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-300 mb-1.5">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                    placeholder="请输入你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-300 mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                    placeholder="请输入你的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-300 mb-1.5">
                  主题 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                  placeholder="请输入咨询主题"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-300 mb-1.5">
                  留言内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors duration-200 resize-none"
                  placeholder="请输入你的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-600 mt-1 text-right">最多500字</p>
              </div>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <button
                type="submit"
                className="w-full px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                发送留言
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}